import { ErrorHandler, inject, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import {
  HttpClient,
  HttpClientModule,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';

import { SharedModule } from './modules/shared/shared.module';
import { JwtModule } from '@auth0/angular-jwt';
import { tokenStorageName } from './models/resources/common';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { APOLLO_OPTIONS, provideApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { InMemoryCache } from '@apollo/client/core';
import { baseServer } from './models/core/endpoints.model';
import { GlobalErrorHandler } from './services/error.handler';
import { FooterComponent } from './components/footer/footer.component';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';

import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';
import { NotfoundComponent } from './components/exception/notfound/notfound/notfound.component';
import { BoardNotFoundComponent } from './components/exception/notfound/board-not-found/board-not-found.component';
import { InternalErrorComponent } from './components/exception/internal/internal-error/internal-error.component';
import { GroupForbiddenComponent } from './components/exception/forbidden/group-forbidden/group-forbidden.component';

import { MatTreeModule } from '@angular/material/tree';
import { HttpCancelService } from './services/common/http-cancel.service';
import { ManageHttpInterceptor } from './services/common/managehttp.interceptor';
import { provideLottieOptions } from 'ngx-lottie';
import { provideLoadingBar } from '@ngx-loading-bar/core';

@NgModule({
  declarations: [
    AppComponent,
    FooterComponent,
    NotfoundComponent,
    BoardNotFoundComponent,
    InternalErrorComponent,
    GroupForbiddenComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    SharedModule,
    ToastrModule.forRoot({
      positionClass: 'toast-bottom-left',
      progressBar: true,
      extendedTimeOut: 5000,
    }),
    TranslateModule.forRoot({
      defaultLanguage: 'en',
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    JwtModule.forRoot({
      config: {
        tokenGetter: tokenGetter,
        allowedDomains: [
          'localhost:44363',
          'localhost:45363',
          'https://172.17.209.15:45363',
          'https://172.17.209.15',
          '172.17.209.15:45363',
          'dcew1184test001.gfk.com:44363',
          'https://evoweb.gfk.at',
          'https://evoweb.at.evo.gfk.com',
        ],
        disallowedRoutes: [
          'https://evoweb.gfk.at/streamingDashboardNext/api/v2/users/current/dashboards?culture=en',
          'https://evoweb.at.evo.gfk.com/streamingDashboardNext/api/v2/users/current/dashboards?culture=en',
        ],
      },
    }),
    HttpClientModule,
  ],
  providers: [
    provideApollo(() => {
      const httpLink = inject(HttpLink);

      return {
        link: httpLink.create({ uri: baseServer }),
        cache: new InMemoryCache(),
        // other options...
      };
    }),
    // {
    //   provide: APOLLO_OPTIONS,
    //   useFactory: (httpLink: HttpLink) => {
    //     return {
    //       cache: new InMemoryCache(),
    //       link: httpLink.create({
    //         uri: baseServer,
    //       }),
    //     };
    //   },
    //   deps: [HttpLink],
    // },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    HttpCancelService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ManageHttpInterceptor,
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}

// ngx-lottie
export function playerFactory() {
  return import('lottie-web/build/player/lottie_svg');
}

// ngx-translate
export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, './assets/localization/', '.json');
}

// JWT token interceptor
export function tokenGetter() {
  return localStorage.getItem(tokenStorageName);
}
